import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import Box from '../Box'
import { ActivityCardProps } from '../../types/activity.types'
import { ActivityMetadata } from './ActivityMetadata'
import { getActivityMetadata } from '../../config/activity.config'
import { Activity, Client } from '../../../../pure-js/types/GrooverTypes'
import { resolveActivityPageFieldVisibility } from '../../../../pure-js/libs/clientHelper'
import { STYLE_COLORS, getContrastTextColor, getActivityDisplayStatus, getWeekdayIndex } from './ViewHelper'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import { RegistrationStatusPill } from './RegistrationStatusPill'
import { useCurrency } from '../../hooks/useCurrency'
import { getThemeConfig, resolveActivityHoverBackground } from '../../themeConfig'
import { formatPrice } from '../../utils/activityUtils'
import { resolveActivityEndTime } from '../../../../pure-js/libs/ActivityTimeHelper'

const LOCALE_MAP: Record<string, string> = { sv: 'sv-SE', en: 'en-GB', no: 'nb-NO' }

// Returns short weekday abbreviation (e.g. "Mån", "Mon", "Man") using the browser Intl API
// dayIndex: 0 = Monday … 6 = Sunday (ISO weekday order, matching getWeekdayIndex)
const shortWeekday = (dayIndex: number, lang = 'sv'): string => {
  const locale = LOCALE_MAP[lang] || 'sv-SE'
  // 2024-01-01 is a Monday → dayIndex 0
  const date = new Date(2024, 0, 1 + dayIndex)
  return date
    .toLocaleDateString(locale, { weekday: 'short' })
    .replace(/\.$/, '') // strip trailing period some locales add
    .replace(/^\w/, (c) => c.toUpperCase())
}

const resolveLocationName = (activity: Activity, client?: Client | null): string => {
  if (!activity.locationId || !client?.locations) return ''
  return client.locations.find((l) => l.id === activity.locationId)?.name ?? ''
}

const extractWeekdays = (activity: Activity, lang = 'sv'): string => {
  const indices = new Set<number>()
  if (activity.occations && activity.occations.length > 0) {
    activity.occations.filter((o) => !o.cancelled).forEach((o) => indices.add(getWeekdayIndex(o.startDate)))
  }
  if (indices.size === 0 && activity.startDate) {
    indices.add(getWeekdayIndex(activity.startDate))
  }
  return Array.from(indices)
    .sort((a, b) => a - b)
    .map((i) => shortWeekday(i, lang))
    .join('/')
}

const CardBannerImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 160px;
  object-fit: cover;
  object-position: left center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  display: block;
`

const CardTop = styled.div<{ $stylecolor: string; $bannerurl?: string }>`
  width: 100%;
  /* height removed to allow image to adapt */
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  position: relative;
  overflow: hidden;
  background: ${(props) => (props.$bannerurl ? 'none' : props.$stylecolor)};
  display: flex;
  align-items: stretch;
  justify-content: stretch;
`

export const ActivityCard: React.FC<ActivityCardProps> = ({ activity, onClick, client, fields }) => {
  const currency = useCurrency(client || undefined)
  const activityPageVisibility = resolveActivityPageFieldVisibility(client ?? undefined)
  const themeConfig = getThemeConfig()
  const styleColor = activity.style
    ? client?.themeConfig?.danceStyles?.find((ds) => ds.name.toLowerCase() === activity.style.toLowerCase())?.color ||
      STYLE_COLORS[activity.style.toLowerCase()] ||
      STYLE_COLORS.default
    : STYLE_COLORS.default

  const textColor = getContrastTextColor(styleColor)
  const hasBanner = Boolean(activity.bannerUrl)
  const displayStatus = getActivityDisplayStatus(activity)

  return (
    <StyledActivityCard
      onClick={onClick}
      fullWidth
      gap="0px"
      $stylecolor={styleColor}
      $textcolor={textColor}
      $activityboxbackground={themeConfig.activityBoxBackground}
      $textcolorconfig={themeConfig.textColor}
      $activityboxborder={themeConfig.activityBoxBorder}
      $activityhover={resolveActivityHoverBackground(themeConfig)}
      key={`activity-${activity.id}`}
      style={{ position: 'relative' }}
    >
      <CardTop $stylecolor={styleColor} $bannerurl={activity.bannerUrl}>
        {activity.bannerUrl && <CardBannerImg src={activity.bannerUrl} alt={activity.name} />}
      </CardTop>
      {displayStatus && (
        <RegistrationStatusPill
          bg={displayStatus.bg}
          color={displayStatus.color}
          icon={displayStatus.icon}
          text={displayStatus.text ?? ''}
          absolute
          top={2}
          left={2}
        />
      )}
      <ActivityCardContent>
        <ActivityTitle>{activity.name}</ActivityTitle>
        <ActivityMetadata
          items={getActivityMetadata(activity, currency, activityPageVisibility, fields)}
          styleColor={styleColor}
        />
      </ActivityCardContent>
    </StyledActivityCard>
  )
}

// Simple List View Card — same 3-row layout as compact, with more padding
export const ActivityCardListView: React.FC<ActivityCardProps> = ({ activity, onClick, client, fields }) => {
  const currency = useCurrency(client || undefined)
  const activityPageVisibility = resolveActivityPageFieldVisibility(client ?? undefined)
  const [searchParams] = useSearchParams()
  const lang = searchParams.get('lang') || 'sv'
  const themeConfig = getThemeConfig()
  const styleColor = activity.style
    ? client?.themeConfig?.danceStyles?.find((ds) => ds.name.toLowerCase() === activity.style.toLowerCase())?.color ||
      STYLE_COLORS[activity.style.toLowerCase()] ||
      STYLE_COLORS.default
    : STYLE_COLORS.default

  const rawFields = fields && fields.length > 0 ? fields : null

  const showInstructors = rawFields
    ? rawFields.includes('instructors')
    : (activityPageVisibility.showInstructors ?? true)
  const showLocation = rawFields ? rawFields.includes('location') : (activityPageVisibility.showLocation ?? true)
  const showCity = rawFields ? rawFields.includes('city') : true
  const showRoom = rawFields ? rawFields.includes('room') : (activityPageVisibility.showRoom ?? false)
  const showLevel = rawFields ? rawFields.includes('level') : true
  const showWeekdays = rawFields ? rawFields.includes('weekdays') : false
  const showTime = rawFields ? rawFields.includes('time') : true
  const showOccasions = rawFields ? rawFields.includes('occasions') : true
  const showPrice = rawFields ? rawFields.includes('price') : (activityPageVisibility.showPrice ?? true)

  const venueName = resolveLocationName(activity, client)

  const startDateObj = new Date(activity.startDate)
  const formattedDate = Number.isFinite(startDateObj.getTime())
    ? `${startDateObj.getDate()}/${startDateObj.getMonth() + 1}`
    : ''
  const resolvedEndTime = resolveActivityEndTime(activity)
  const timeDisplay = resolvedEndTime ? `${activity.time}–${resolvedEndTime}` : activity.time
  const weekdayStr = extractWeekdays(activity, lang)
  const occasionCount = activity.occations?.length ?? activity.noOfOccasions

  const scheduleParts: string[] = []
  if (showWeekdays && weekdayStr) scheduleParts.push(weekdayStr)
  if (formattedDate) scheduleParts.push(formattedDate)
  if (showOccasions && occasionCount != null && occasionCount > 0) scheduleParts.push(`${occasionCount}×`)

  const hasSubtitleRow =
    (showInstructors && !!activity.instructorsNames) ||
    (showLocation && !!venueName) ||
    (showCity && !!activity.city && (!venueName || !showLocation)) ||
    (showRoom && !!activity.place)

  const displayStatus = getActivityDisplayStatus(activity)

  return (
    <ListViewCard
      onClick={onClick}
      $stylecolor={styleColor}
      $activityboxbackground={themeConfig.activityBoxBackground}
      $textcolorconfig={themeConfig.textColor}
    >
      {activity.bannerUrl && <ListViewBannerImg src={activity.bannerUrl} alt={activity.name} />}

      {/* Row 1: Name + Level badge */}
      <ListViewRow $justify="space-between" $align="flex-start" $gap="8px">
        <ListViewTitle $textcolorconfig={themeConfig.textColor}>{activity.name}</ListViewTitle>
        {showLevel && activity.level && (
          <CompactViewLevelBadge $stylecolor={styleColor}>{activity.level}</CompactViewLevelBadge>
        )}
      </ListViewRow>

      {/* Row 2: Instructor · Location */}
      {hasSubtitleRow && (
        <ListViewRow $gap="10px" $mt="4px" $wrap>
          {showInstructors && activity.instructorsNames && (
            <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>
              <PersonOutlinedIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
              {activity.instructorsNames}
            </CompactViewMetaItem>
          )}
          {showLocation && venueName && (
            <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={venueName}>
              <LocationOnIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
              {venueName}
            </CompactViewMetaItem>
          )}
          {!venueName && showCity && activity.city && (
            <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={activity.city}>
              <LocationOnIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
              {activity.city}
            </CompactViewMetaItem>
          )}
          {venueName && showCity && !showLocation && activity.city && (
            <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={activity.city}>
              <LocationOnIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
              {activity.city}
            </CompactViewMetaItem>
          )}
          {showRoom && activity.place && (
            <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={activity.place}>
              <LocationOnIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
              {activity.place}
            </CompactViewMetaItem>
          )}
        </ListViewRow>
      )}

      {/* Row 3: Schedule + Price */}
      <ListViewRow $justify="space-between" $align="center" $mt="6px">
        <ListViewScheduleGroup>
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>
            <CalendarTodayIcon sx={{ fontSize: '0.85rem', opacity: 0.6 }} />
            {scheduleParts.join(' · ')}
          </CompactViewMetaItem>
          {showTime && timeDisplay && (
            <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>
              <AccessTimeIcon sx={{ fontSize: '0.85rem', opacity: 0.6 }} />
              {timeDisplay}
            </CompactViewMetaItem>
          )}
        </ListViewScheduleGroup>
        {showPrice && activity.price != null && activity.eventType !== 'Parentevent' && (
          <CompactViewPriceField $textcolorconfig={themeConfig.textColor}>
            {formatPrice(activity.price, currency)}
          </CompactViewPriceField>
        )}
      </ListViewRow>

      {displayStatus && (
        <ListViewStatus $bg={displayStatus.bg} $color={displayStatus.color}>
          {displayStatus.icon}
          {displayStatus.text}
        </ListViewStatus>
      )}
    </ListViewCard>
  )
}

// Compact View Card — single-row, wraps only when necessary
export const ActivityCardCompact: React.FC<ActivityCardProps> = ({ activity, onClick, client, fields }) => {
  const currency = useCurrency(client || undefined)
  const activityPageVisibility = resolveActivityPageFieldVisibility(client ?? undefined)
  const [searchParams] = useSearchParams()
  const lang = searchParams.get('lang') || 'sv'
  const themeConfig = getThemeConfig()
  const styleColor = activity.style
    ? client?.themeConfig?.danceStyles?.find((ds) => ds.name.toLowerCase() === activity.style.toLowerCase())?.color ||
      STYLE_COLORS[activity.style.toLowerCase()] ||
      STYLE_COLORS.default
    : STYLE_COLORS.default

  // fields=[] means "user explicitly cleared all fields" — fall back to a sensible default
  const rawFields = fields && fields.length > 0 ? fields : null

  const showInstructors = rawFields
    ? rawFields.includes('instructors')
    : (activityPageVisibility.showInstructors ?? true)
  const showLocation = rawFields ? rawFields.includes('location') : (activityPageVisibility.showLocation ?? true)
  const showCity = rawFields ? rawFields.includes('city') : true
  const showRoom = rawFields ? rawFields.includes('room') : (activityPageVisibility.showRoom ?? false)
  const showLevel = rawFields ? rawFields.includes('level') : true
  const showWeekdays = rawFields ? rawFields.includes('weekdays') : false
  const showTime = rawFields ? rawFields.includes('time') : true
  const showOccasions = rawFields ? rawFields.includes('occasions') : true
  const showPrice = rawFields ? rawFields.includes('price') : (activityPageVisibility.showPrice ?? true)

  const venueName = resolveLocationName(activity, client)

  const startDateObj = new Date(activity.startDate)
  const formattedDate = Number.isFinite(startDateObj.getTime())
    ? `${startDateObj.getDate()}/${startDateObj.getMonth() + 1}`
    : ''
  const resolvedEndTime = resolveActivityEndTime(activity)
  const timeDisplay = resolvedEndTime ? `${activity.time}–${resolvedEndTime}` : activity.time
  const weekdayStr = extractWeekdays(activity, lang)
  const occasionCount = activity.occations?.length ?? activity.noOfOccasions

  return (
    <CompactViewCard
      onClick={onClick}
      $stylecolor={styleColor}
      $activityboxbackground={themeConfig.activityBoxBackground}
      $textcolorconfig={themeConfig.textColor}
    >
      {/* Row 1: Name + Level badge */}
      <CompactViewTopRow>
        <CompactViewNameCol>
          <CompactViewTitle $textcolorconfig={themeConfig.textColor}>{activity.name}</CompactViewTitle>
        </CompactViewNameCol>
        {showLevel && activity.level && (
          <CompactViewLevelBadge $stylecolor={styleColor}>{activity.level}</CompactViewLevelBadge>
        )}
      </CompactViewTopRow>

      {/* Row 2: All other meta fields */}
      <CompactViewFieldsRow>
        {showInstructors && activity.instructorsNames && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>
            {activity.instructorsNames}
          </CompactViewMetaItem>
        )}
        {showLocation && venueName && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={venueName}>
            {venueName}
          </CompactViewMetaItem>
        )}
        {!venueName && showCity && activity.city && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={activity.city}>
            {activity.city}
          </CompactViewMetaItem>
        )}
        {venueName && showCity && !showLocation && activity.city && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={activity.city}>
            {activity.city}
          </CompactViewMetaItem>
        )}
        {showRoom && activity.place && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor} title={activity.place}>
            {activity.place}
          </CompactViewMetaItem>
        )}
        {showWeekdays && weekdayStr && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>{weekdayStr}</CompactViewMetaItem>
        )}
        {formattedDate && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>{formattedDate}</CompactViewMetaItem>
        )}
        {showTime && timeDisplay && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>{timeDisplay}</CompactViewMetaItem>
        )}
        {showOccasions && occasionCount != null && occasionCount > 0 && (
          <CompactViewMetaItem $textcolorconfig={themeConfig.textColor}>{occasionCount}×</CompactViewMetaItem>
        )}
        {showPrice && activity.price != null && activity.eventType !== 'Parentevent' && (
          <CompactViewPriceField $textcolorconfig={themeConfig.textColor}>
            {formatPrice(activity.price, currency)}
          </CompactViewPriceField>
        )}
      </CompactViewFieldsRow>
    </CompactViewCard>
  )
}

const STYLED_ACTIVITY_CARD_TRANSIENT_PROPS = [
  '$stylecolor',
  '$textcolor',
  '$activityboxbackground',
  '$textcolorconfig',
  '$activityboxborder',
  '$activityhover'
]
const StyledActivityCard = styled(Box, {
  shouldForwardProp: (prop) => !STYLED_ACTIVITY_CARD_TRANSIENT_PROPS.includes(prop)
})<{
  $stylecolor: string
  $textcolor: string
  $activityboxbackground: string
  $textcolorconfig: string
  $activityboxborder: string
  $activityhover: string
}>`
  color: ${(props) => props.$textcolorconfig || '#222'} !important;
  border-radius: 8px;
  border: 2px solid ${(props) => props.$activityboxborder || props.$stylecolor};
  cursor: pointer;
  background: ${(props) => props.$activityboxbackground || '#fff'};
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  &:hover {
    border-color: ${(props) => props.$stylecolor};
    background: ${(props) => props.$activityhover || props.$activityboxbackground || '#fff'};
  }
`

const ActivityCardContent = styled(Box)`
  padding: 8px 16px;
  gap: 8px;
  margin-bottom: 8px;
`

const ActivityTitle = styled.p`
  margin: 0;
  font-weight: 600;
`

// List View Styles — same card shape as compact, but with more vertical padding
const ListViewCard = styled.div<{ $stylecolor: string; $activityboxbackground: string; $textcolorconfig: string }>`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.$activityboxbackground || '#fff'};
  border-left: 4px solid ${(props) => props.$stylecolor};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  box-shadow: none;
  outline: 1.5px solid transparent;
  outline-offset: -2px;
  transition: outline-color 0.15s ease;

  &:hover {
    outline-color: ${(props) => props.$stylecolor};
  }
`

const ListViewBannerImg = styled.img`
  height: auto;
  max-height: 75px;
  width: 275px;
  max-width: 100%;
  object-fit: contain;
  object-position: left;
  border-radius: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
  display: block;
`

const ListViewRow = styled.div<{
  $justify?: string
  $align?: string
  $gap?: string
  $mt?: string
  $wrap?: boolean
}>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => props.$justify || 'flex-start'};
  align-items: ${(props) => props.$align || 'center'};
  gap: ${(props) => props.$gap || '6px'};
  margin-top: ${(props) => props.$mt || '0'};
  flex-wrap: ${(props) => (props.$wrap ? 'wrap' : 'nowrap')};
`

const ListViewTitle = styled.div<{ $textcolorconfig: string }>`
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => props.$textcolorconfig || '#222'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`

const ListViewScheduleGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`

const ListViewStatus = styled.div<{ $bg: string; $color: string }>`
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.8em;
  font-weight: 400;
  border-radius: 16px;
  padding: 2px 10px;
  min-height: 20px;
  margin-top: 6px;
  align-self: flex-start;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.18),
    0 1.5px 4px rgba(0, 0, 0, 0.12);
`

// Compact View Styles — flat single-row card, 2-row on mobile
const CompactViewCard = styled.div<{ $stylecolor: string; $activityboxbackground: string; $textcolorconfig: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: ${(props) => props.$activityboxbackground || '#fff'};
  border-left: 4px solid ${(props) => props.$stylecolor};
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: background 0.15s ease;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 8px 12px;
  }

  &:hover {
    background: ${() => resolveActivityHoverBackground(getThemeConfig())};
  }
`

const CompactViewTopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
  width: 100%;
`

const CompactViewNameCol = styled.div`
  flex: 1;
  min-width: 120px;
  overflow: hidden;
`

const CompactViewTitle = styled.div<{ $textcolorconfig: string }>`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(props) => props.$textcolorconfig || '#222'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CompactViewFieldsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 4px 12px;
  flex-shrink: 1;
  min-width: 0;

  @media (max-width: 600px) {
    justify-content: flex-start;
    width: 100%;
  }
`

const CompactViewLevelBadge = styled.div<{ $stylecolor: string }>`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${(props) => props.$stylecolor};
  background: ${(props) => `${props.$stylecolor}1a`};
  border: 1px solid ${(props) => `${props.$stylecolor}4d`};
  border-radius: 16px;
  padding: 1px 8px;
  white-space: nowrap;
  flex-shrink: 0;
`

const CompactViewMetaItem = styled.div<{ $textcolorconfig: string }>`
  font-size: 0.8rem;
  font-weight: 400;
  color: ${(props) => {
    const base = props.$textcolorconfig || '#444'
    if (base === '#ffffff' || base === '#fff') return 'rgba(255,255,255,0.75)'
    return base
  }};
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
  flex-shrink: 0;
`

const CompactViewPriceField = styled.div<{ $textcolorconfig: string }>`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(props) => {
    const base = props.$textcolorconfig || '#222'
    if (base === '#ffffff' || base === '#fff') return 'rgba(255,255,255,0.95)'
    return base
  }};
  flex-shrink: 0;
  white-space: nowrap;
`
